/** Weld completion date/time helpers for Floor Sheet `fsTimes`. */

export type FloorSheetFsTimeSlot = {
  date: string // YYYY-MM-DD
  time: string // HH:mm (24h)
}

export type FloorSheetFsTimes = {
  rootComplete: FloorSheetFsTimeSlot
  hotPassStart: FloorSheetFsTimeSlot
  weldCompleted: FloorSheetFsTimeSlot
}

export type FloorSheetFsTimeKey = keyof FloorSheetFsTimes

/** How the current fsTimes were last populated. */
export type FloorSheetFsTimesSource = 'manual' | 'data360-logs' | null

export function emptyFsTimeSlot(): FloorSheetFsTimeSlot {
  return { date: '', time: '' }
}

export function emptyFsTimes(): FloorSheetFsTimes {
  return {
    rootComplete: emptyFsTimeSlot(),
    hotPassStart: emptyFsTimeSlot(),
    weldCompleted: emptyFsTimeSlot()
  }
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

/** Parse legacy free-text or structured slot into date/time. */
export function normalizeFsTimeSlot(raw: unknown): FloorSheetFsTimeSlot {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>
    return {
      date: String(o.date ?? '').trim(),
      time: String(o.time ?? '').trim().slice(0, 5)
    }
  }
  const text = String(raw ?? '').trim()
  if (!text) return emptyFsTimeSlot()
  // ISO-ish: 2026-08-14T08:40 or 2026-08-14 08:40
  const iso = text.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{1,2}:\d{2})/)
  if (iso) {
    return { date: iso[1], time: iso[2].padStart(5, '0').slice(0, 5) }
  }
  // Keep date-only if present
  const dateOnly = text.match(/^(\d{4}-\d{2}-\d{2})$/)
  if (dateOnly) return { date: dateOnly[1], time: '' }
  // Unparseable legacy text — leave blank structured fields (display falls back via format)
  return emptyFsTimeSlot()
}

export function normalizeFsTimes(raw: unknown): FloorSheetFsTimes {
  const o = asRecord(raw)
  return {
    rootComplete: normalizeFsTimeSlot(o?.rootComplete),
    hotPassStart: normalizeFsTimeSlot(o?.hotPassStart),
    weldCompleted: normalizeFsTimeSlot(o?.weldCompleted)
  }
}

/**
 * When a date is set on one slot, copy it into other slots that still have an empty date.
 * Never copies time.
 */
export function syncFsTimesDate(
  times: FloorSheetFsTimes,
  changedKey: FloorSheetFsTimeKey,
  nextDate: string
): FloorSheetFsTimes {
  const next: FloorSheetFsTimes = {
    ...times,
    [changedKey]: { ...times[changedKey], date: nextDate }
  }
  if (!nextDate.trim()) return next
  const keys: FloorSheetFsTimeKey[] = ['rootComplete', 'hotPassStart', 'weldCompleted']
  for (const key of keys) {
    if (key === changedKey) continue
    if (!next[key].date.trim()) {
      next[key] = { ...next[key], date: nextDate }
    }
  }
  return next
}

export function patchFsTimeSlot(
  times: FloorSheetFsTimes,
  key: FloorSheetFsTimeKey,
  patch: Partial<FloorSheetFsTimeSlot>
): FloorSheetFsTimes {
  if (patch.date !== undefined) {
    return syncFsTimesDate(
      { ...times, [key]: { ...times[key], ...patch } },
      key,
      patch.date
    )
  }
  return {
    ...times,
    [key]: { ...times[key], ...patch }
  }
}

/** CRC-style display, e.g. `8/14/26 at 8:40 AM`. Empty parts omitted. */
export function formatFsTimeSlotDisplay(slot: FloorSheetFsTimeSlot): string {
  const datePart = formatFsDateShort(slot.date)
  const timePart = formatFsTimeAmPm(slot.time)
  if (datePart && timePart) return `${datePart} at ${timePart}`
  if (datePart) return datePart
  if (timePart) return timePart
  return ''
}

function formatFsDateShort(isoDate: string): string {
  const m = isoDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return ''
  const y = Number(m[1]) % 100
  const mo = Number(m[2])
  const d = Number(m[3])
  return `${mo}/${d}/${String(y).padStart(2, '0')}`
}

function formatFsTimeAmPm(hhmm: string): string {
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return ''
  let h = Number(m[1])
  const min = m[2]
  if (!Number.isFinite(h) || h < 0 || h > 23) return ''
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12
  if (h === 0) h = 12
  return `${h}:${min} ${ampm}`
}
